import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ServiceItem } from '../../core/config/site-config.interface';

@Component({
  selector: 'app-service-detail',
  standalone: true,
  imports: [RouterLink],
  template: `
    @if (service) {
      <!-- Hero -->
      <section class="relative min-h-[400px] md:min-h-[480px] flex items-end -mt-20 overflow-hidden">
        @if (service.image) {
          <img [src]="service.image" [alt]="service.title" class="absolute inset-0 w-full h-full object-cover" />
        } @else {
          <div class="absolute inset-0" [style.background]="service.gradient"></div>
        }
        <div class="absolute inset-0 bg-gradient-to-t from-surface-950/90 via-surface-950/40 to-surface-950/20"></div>
        <div class="relative z-10 w-full pb-12 pt-40 md:pt-48">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <a routerLink="/services" class="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-6 transition-colors">
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
              All Services
            </a>
            <div class="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm mb-5">
              <span class="text-2xl" [innerHTML]="service.icon"></span>
            </div>
            <h1 class="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight max-w-3xl">
              {{ service.title }}
            </h1>
          </div>
        </div>
      </section>

      <!-- Content -->
      <section class="py-16 md:py-20 bg-white">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            <!-- Main content -->
            <div class="lg:col-span-2">
              <p class="text-lg md:text-xl text-surface-600 leading-relaxed mb-10 font-medium">
                {{ service.desc }}
              </p>

              @if (service.content) {
                <div class="service-prose" [innerHTML]="service.content"></div>
              }
            </div>

            <!-- Sidebar -->
            <aside>
              <div class="sticky top-24 space-y-6">
                <!-- Features card -->
                @if (service.features && service.features.length) {
                  <div class="p-6 rounded-xl bg-surface-50 border border-surface-100">
                    <h3 class="font-heading text-sm font-bold uppercase tracking-wider text-surface-400 mb-5">What's Included</h3>
                    <ul class="space-y-3">
                      @for (f of service.features; track f) {
                        <li class="flex items-start gap-3 text-sm text-surface-700">
                          <svg aria-hidden="true" class="w-4 h-4 text-brand-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                          {{ f }}
                        </li>
                      }
                    </ul>
                  </div>
                }

                <!-- CTA card -->
                <div class="p-6 rounded-xl bg-brand-600 text-white">
                  <h3 class="font-heading text-lg font-bold mb-2">Ready to Get Started?</h3>
                  <p class="text-brand-100 text-sm mb-5 leading-relaxed">Get a free, no-obligation estimate for your project.</p>
                  <a routerLink="/contact" class="inline-flex items-center gap-2 w-full justify-center px-6 py-3 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors">
                    Get Free Estimate
                    <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </a>
                </div>

                <!-- Phone -->
                <div class="p-5 rounded-xl bg-surface-50 border border-surface-100 text-center">
                  <p class="text-xs text-surface-400 mb-1">Call us directly</p>
                  <a [href]="'tel:' + phone.replace(/[^+\\d]/g, '')" class="font-heading text-lg font-bold text-surface-900 hover:text-brand-600 transition-colors">
                    {{ phone }}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <!-- Other Services -->
      @if (otherServices.length) {
        <section class="py-16 md:py-20 bg-surface-50 border-t border-surface-100">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="flex items-center justify-between mb-10">
              <h2 class="font-heading text-xl md:text-2xl font-bold text-surface-900">Other Services</h2>
              <a routerLink="/services" class="group inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                View All
                <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              @for (s of otherServices; track s.slug) {
                <a [routerLink]="'/services/' + s.slug"
                   class="group rounded-xl overflow-hidden bg-white border border-surface-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  @if (s.image) {
                    <div class="aspect-[16/10] overflow-hidden">
                      <img [src]="s.image" [alt]="s.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  }
                  <div class="p-5">
                    <div class="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand-50 text-brand-600 mb-3">
                      <span class="text-lg" [innerHTML]="s.icon"></span>
                    </div>
                    <h3 class="font-heading text-base font-bold text-surface-900 mb-1 group-hover:text-brand-600 transition-colors">{{ s.title }}</h3>
                    <p class="text-sm text-surface-500 line-clamp-2">{{ s.desc }}</p>
                  </div>
                </a>
              }
            </div>
          </div>
        </section>
      }

      <!-- CTA -->
      <section class="py-16 bg-brand-600 relative overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500"></div>
        <div class="mx-auto max-w-4xl px-6 lg:px-8 text-center relative z-10">
          <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Let's Discuss Your Project</h2>
          <p class="text-brand-100 mb-8 max-w-lg mx-auto">Get expert advice and a free estimate. We're here to help.</p>
          <div class="flex flex-wrap items-center justify-center gap-4">
            <a routerLink="/contact" class="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors shadow-lg shadow-brand-900/20">
              Get Free Estimate
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
            <a routerLink="/projects" class="inline-flex items-center gap-2 px-7 py-3.5 border border-white/25 text-white font-semibold text-sm rounded-lg hover:bg-white/10 transition-colors">
              View Our Work
            </a>
          </div>
        </div>
      </section>
    } @else {
      <section class="py-32 text-center -mt-20 pt-52">
        <div class="mx-auto max-w-md px-6">
          <div class="w-16 h-16 rounded-full bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-6">
            <svg aria-hidden="true" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h1 class="font-heading text-2xl font-bold text-surface-900 mb-3">Service Not Found</h1>
          <p class="text-surface-500 mb-6">The service you're looking for doesn't exist.</p>
          <a routerLink="/services" class="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
            <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
            Browse All Services
          </a>
        </div>
      </section>
    }
  `,
  styles: [`
    :host ::ng-deep .service-prose {
      h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--color-surface-900);
        margin-top: 2.5rem;
        margin-bottom: 1rem;
        line-height: 1.25;
      }
      p {
        color: var(--color-surface-600);
        line-height: 1.8;
        margin-bottom: 1.25rem;
        font-size: 1.0625rem;
      }
      ul, ol {
        padding-left: 1.5rem;
        margin-bottom: 1.25rem;
        color: var(--color-surface-600);
      }
      ul { list-style-type: disc; }
      ol { list-style-type: decimal; }
      li {
        margin-bottom: 0.5rem;
        line-height: 1.7;
        font-size: 1.0625rem;
      }
      strong { color: var(--color-surface-900); }
      blockquote {
        border-left: 3px solid var(--color-brand-500);
        padding: 1rem 1.5rem;
        margin: 2rem 0;
        font-style: italic;
        color: var(--color-surface-700);
        background: var(--color-surface-50);
        border-radius: 0 0.75rem 0.75rem 0;
      }
    }
  `],
})
export class ServiceDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  service: ServiceItem | undefined;
  otherServices: ServiceItem[] = [];
  phone = this.config.contact.phone;

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('slug');
    const allServices = this.config.pages.services.services;
    this.service = allServices.find(s => s.slug === slug);

    if (this.service) {
      this.seo.setTitle(this.service.title);
      this.seo.setDescription(this.service.desc);
      this.seo.setJsonLd([
        this.seo.getServiceSchema(this.service.title, this.service.desc),
        this.seo.getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: this.service.title, url: '/services/' + this.service.slug },
        ]),
      ]);

      this.otherServices = allServices.filter(s => s.slug !== slug).slice(0, 3);
    }
  }
}
